import React, { useState, useMemo } from 'react';
import {
  FaTimes,
  FaSearch,
  FaUserNurse,
  FaCalendarAlt,
  FaProjectDiagram,
  FaHospital,
  FaHome,
  FaExclamationTriangle,
  FaClipboardList,
  FaInfoCircle,
} from 'react-icons/fa';
import styles from './IssueDetailModal.module.css';

// Turkish labels for the issueCategory codes used in the visit evaluation form.
const ISSUE_CATEGORY_LABELS = {
  iv_access: 'IV Erişim',
  patient_compliance: 'Hasta Uyumu',
  documentation: 'Dokümantasyon',
  logistics: 'Lojistik',
  protocol: 'Protokol',
  equipment: 'Ekipman',
  other: 'Diğer',
};

// Each "flag" describes one kind of problem a visit can carry. We render a chip
// for every flag that is active on a visit so the SMO can see, at a glance,
// what went wrong.
const FLAGS = [
  { key: 'issueOccurred', label: 'Sorun', tone: 'danger' },
  { key: 'issueResultedInMiss', label: 'Kaçırılan', tone: 'danger' },
  { key: 'protocolDeviation', label: 'Protokol Sapması', tone: 'warning' },
  { key: 'capaOpened', label: 'CAPA Açıldı', tone: 'warning' },
  { key: 'screenFail', label: 'Tarama Başarısız', tone: 'warning' },
  { key: 'dropPatient', label: 'Hasta Çıkışı', tone: 'warning' },
  { key: 'wasAeSaeTaken', label: 'AE/SAE', tone: 'info' },
  { key: 'dcfCreated', label: 'DCF', tone: 'info' },
];

const isOverdue = (v) => {
  const ev = v.visitEvaluation || {};
  const start = v.date || v.firstVisitDate;
  if (!ev.slaTarget || !ev.processEndDate || !start) return false;
  const cycle = Math.ceil((new Date(ev.processEndDate) - new Date(start)) / 86400000);
  return cycle > parseFloat(ev.slaTarget);
};

const IssueDetailModal = ({ show, onHide, title, visits = [], workers = [], projectNames = {}, isDarkMode = false }) => {
  const [search, setSearch] = useState('');

  const coordName = (v) => {
    const w = workers.find((wk) => wk.id === v.employeeId);
    return w ? `${w.name} ${w.surname}` : (v.employeeName || v.employeeId || 'Bilinmeyen Çalışan');
  };

  // Resolve the human-readable short project title, falling back to any name
  // stored on the visit, and only then to the raw id.
  const projectName = (v) =>
    projectNames[v.projectId] || v.projectName || v.projectId || '';

  // Sort newest-first, then apply the in-modal search filter.
  const rows = useMemo(() => {
    const enriched = (visits || []).map((v) => ({
      visit: v,
      who: coordName(v),
      when: v.date || v.firstVisitDate || '',
      project: projectName(v),
    }));
    enriched.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
    const q = search.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter((r) => {
      const ev = r.visit.visitEvaluation || {};
      const cat = ISSUE_CATEGORY_LABELS[ev.issueCategory] || ev.issueCategory || '';
      return (
        r.who.toLowerCase().includes(q) ||
        String(r.project).toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q) ||
        String(ev.issueDetails || '').toLowerCase().includes(q) ||
        String(ev.comments || '').toLowerCase().includes(q) ||
        String(ev.patientId || r.visit.patientId || '').toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, workers, search]);

  if (!show) return null;

  return (
    <div className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`} onClick={onHide}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FaExclamationTriangle className={styles.headerIcon} />
            <div>
              <h3 className={styles.title}>{title}</h3>
              <span className={styles.subtitle}>{rows.length} kayıt</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onHide} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        {/* Search */}
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Çalışan, proje, kategori veya açıklama ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {rows.length === 0 ? (
            <div className={styles.empty}>
              <FaClipboardList className={styles.emptyIcon} />
              <p>Gösterilecek kayıt bulunamadı.</p>
            </div>
          ) : (
            rows.map((r, idx) => {
              const v = r.visit;
              const ev = v.visitEvaluation || {};
              const overdue = isOverdue(v);
              const isHome = v.visitType === 'home';
              const activeFlags = FLAGS.filter((f) => ev[f.key] === 'yes');
              const category = ISSUE_CATEGORY_LABELS[ev.issueCategory] || ev.issueCategory;
              const patientId = ev.patientId || v.patientId;

              return (
                <div key={v.id || idx} className={styles.card}>
                  {/* Card top: who + when */}
                  <div className={styles.cardTop}>
                    <div className={styles.who}>
                      <FaUserNurse className={styles.whoIcon} />
                      <span className={styles.whoName}>{r.who}</span>
                    </div>
                    <div className={styles.when}>
                      <FaCalendarAlt className={styles.whenIcon} />
                      <span>{r.when || 'Tarih yok'}</span>
                    </div>
                  </div>

                  {/* Meta row: visit type, project, patient */}
                  <div className={styles.metaRow}>
                    <span className={`${styles.typeChip} ${isHome ? styles.typeHome : styles.typeSite}`}>
                      {isHome ? <FaHome /> : <FaHospital />}
                      {isHome ? 'Ev Viziti' : 'Hastane Viziti'}
                    </span>
                    {r.project && (
                      <span className={styles.metaItem}>
                        <FaProjectDiagram className={styles.metaIcon} />
                        {r.project}
                      </span>
                    )}
                    {patientId && (
                      <span className={styles.metaItem}>
                        <FaInfoCircle className={styles.metaIcon} />
                        Hasta: {patientId}
                      </span>
                    )}
                  </div>

                  {/* Problem flags */}
                  {(activeFlags.length > 0 || overdue) && (
                    <div className={styles.flags}>
                      {activeFlags.map((f) => (
                        <span key={f.key} className={`${styles.flag} ${styles[`flag_${f.tone}`]}`}>
                          {f.label}
                        </span>
                      ))}
                      {overdue && (
                        <span className={`${styles.flag} ${styles.flag_danger}`}>SLA Gecikmiş</span>
                      )}
                    </div>
                  )}

                  {/* What: category + details */}
                  {(category || ev.issueDetails || ev.comments) && (
                    <div className={styles.detailBlock}>
                      {category && (
                        <div className={styles.detailLine}>
                          <span className={styles.detailKey}>Kaynak / Kategori</span>
                          <span className={styles.detailVal}>{category}</span>
                        </div>
                      )}
                      {ev.issueDetails && (
                        <div className={styles.detailLine}>
                          <span className={styles.detailKey}>Sorun Detayı</span>
                          <span className={styles.detailVal}>{ev.issueDetails}</span>
                        </div>
                      )}
                      {ev.comments && (
                        <div className={styles.detailLine}>
                          <span className={styles.detailKey}>Notlar</span>
                          <span className={styles.detailVal}>{ev.comments}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueDetailModal;
